import TasksModule from '@modules/TasksModule';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import updateInteractionByChannelId, {
  UpdateInteractionByChannelIdInput,
} from './lib/updateInteractionByChannelId';
import { ChannelClassificationInput } from '@api/generated/graphql';
import _ from 'lodash';
import { ITask, TaskState } from '@modules/TasksModule/types';
import { getInteractionRelevantFields } from '@modules/InteractionsModule/helpers';

export class InteractionsModule extends AppModule {
  private throttledUpsertInteraction: any;

  override preload(): void {
    this.throttledUpsertInteraction = _.throttle(
      this.upsertInteraction.bind(this),
      3000,
    );

    this.app.events.on(
      'task.updated',
      async ({ task, field }: { task: ITask; field: string }) => {
        // Fields that are relevant to the interaction record
        const relevantFields = getInteractionRelevantFields();

        // only update interaction if the task is in a recordable state
        // and the changes are relevant to the interaction record
        if (this.isRecordableState(task) && relevantFields.has(field)) {
          try {
            if (field === 'state' && task.state === TaskState.Completed) {
              this.upsertInteraction(task);
            } else {
              // keeping the throttling for updates that are not critical like customer id/name
              // to be backward compatible with the previous implementation until backend implementation is done
              this.throttledUpsertInteraction(task);
            }
          } catch (e) {
            this.app.logger.error('Error writing interaction', e);
          }
        }
      },
    );
  }

  public async writeClassificationInfo(
    task: ITask,
    info: ChannelClassificationInput,
  ) {
    return await this.upsertInteraction(task, { channelClassification: info });
  }

  private isRecordableState(task: ITask): boolean {
    // only record interaction if we have the sid
    if (!task.attributes.sessionSid || task.id !== task.attributes.sessionSid) {
      return false;
    }

    return (
      task.attributes.recordInteraction === true &&
      (task.state === TaskState.Accepted ||
        task.state === TaskState.Completed ||
        task.state === TaskState.WrapUp)
    );
  }

  private async upsertInteraction(
    task: ITask,
    extra: Partial<UpdateInteractionByChannelIdInput> = {},
  ) {
    if (!task.tenantId) {
      return;
    }

    this.app.logger.info('Interaction sync', task.id, task.state);

    const interactionData: UpdateInteractionByChannelIdInput = {
      customerId: task.attributes.customerId || undefined,
      customerName:
        (task.attributes.customerId && task.displayName) || undefined,
      ...extra,
    };

    const interaction = await updateInteractionByChannelId(
      task.id,
      task.tenantId,
      interactionData,
    );
    if (!task.attributes.interactionId) {
      TasksModule.setTaskAttribute(task.id, 'interactionId', interaction.id);
      TasksModule.setTaskAttribute(
        task.id,
        'displayId',
        interaction?.displayId,
      );
    }
    return interaction;
  }
}

export default new InteractionsModule(
  appManager.events,
  appManager.logger('Interactions'),
);
